import React from 'react';
import { cn } from '@/lib/utils';
import { parseEventDateValue } from '../utils/dateTime';

type EventBadgeVariant = 'today' | 'tomorrow' | 'yesterday';

type EventBadgeState = {
  show: boolean;
  label: string;
  variant: EventBadgeVariant;
};

const COLOR_MAP: Record<EventBadgeVariant, { pulse: string; dot: string; label: string }> = {
  today: {
    pulse: 'bg-[#FF4B4B]',
    dot: 'bg-[#FF4B4B]',
    label: 'bg-[#FF4B4B]',
  },
  tomorrow: {
    pulse: 'bg-[#FF9F1C]',
    dot: 'bg-[#FF9F1C]',
    label: 'bg-[#FF9F1C]',
  },
  yesterday: {
    pulse: 'bg-[#4C6FFF]',
    dot: 'bg-[#4C6FFF]',
    label: 'bg-[#4C6FFF]',
  },
};

export function getEventBadgeState(eventDateInput?: string | Date | null): EventBadgeState {
  const eventDate = eventDateInput instanceof Date
    ? eventDateInput
    : typeof eventDateInput === 'string'
      ? parseEventDateValue(eventDateInput)
      : null;

  if (!eventDate) {
    return { show: false, label: '', variant: 'today' };
  }

  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const ONE_DAY_MS = 1000 * 60 * 60 * 24;
  const diffDays = Math.floor((eventDate.getTime() - todayStart.getTime()) / ONE_DAY_MS);

  if (diffDays === 1) {
    return { show: true, label: 'Mañana', variant: 'tomorrow' };
  }

  if (diffDays === 0) {
    return { show: true, label: 'Hoy', variant: 'today' };
  }

  if (diffDays === -1) {
    const hoursSinceMidnight = (now.getTime() - todayStart.getTime()) / (1000 * 60 * 60);
    if (hoursSinceMidnight <= 12) {
      return { show: true, label: 'Hoy', variant: 'today' };
    }
    return { show: true, label: 'Ayer', variant: 'yesterday' };
  }

  return { show: false, label: '', variant: 'today' };
}

type EventPulsingBadgeProps = {
  variant: EventBadgeVariant;
  label: string;
  className?: string;
};

const EventPulsingBadge: React.FC<EventPulsingBadgeProps> = ({ variant, label, className }) => {
  const colors = COLOR_MAP[variant];

  return (
    <div className={cn('flex flex-col items-center', className)}>
      <span className="relative flex h-7 w-7">
        <span className={cn('absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping', colors.pulse)} />
        <span className={cn('relative inline-flex h-7 w-7 rounded-full', colors.dot)} />
      </span>
      <span className={cn('mt-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white shadow-sm', colors.label)}>
        {label}
      </span>
    </div>
  );
};

export default EventPulsingBadge;
